import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Truck, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OrderSuccessPage() {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [pollingCount, setPollingCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  usePageTitle(order ? `Order ${order.order_number}` : 'Order Confirmation');

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const orderRes = await axios.get(`${API_URL}/orders/${orderId}`);
        setOrder(orderRes.data);
        setPaymentStatus(orderRes.data.payment_status);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Poll for payment status if we have a session_id
  useEffect(() => {
    if (!sessionId || paymentStatus === 'paid' || pollingCount >= 10) return;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('paid');
          // Refresh order data
          if (orderId) {
            const orderRes = await axios.get(`${API_URL}/orders/${orderId}`);
            setOrder(orderRes.data);
          }
        } else {
          setPollingCount(prev => prev + 1);
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
        setPollingCount(prev => prev + 1);
      }
    };

    const timer = setTimeout(pollStatus, 2000);
    return () => clearTimeout(timer);
  }, [sessionId, paymentStatus, pollingCount, orderId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#EA580C]" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-transition min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Order not found
          </h1>
          <p className="text-muted-foreground mb-8">
            We couldn't find your order. Please check your email for confirmation.
          </p>
          <Link to="/shop">
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Success Header */}
        <div className="text-center mb-12">
          {paymentStatus === 'paid' ? (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="order-success-title">
                Thank You for Your Order!
              </h1>
              <p className="text-muted-foreground text-lg">
                Your order has been confirmed and is being processed.
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-amber-600 animate-spin" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Processing Payment...
              </h1>
              <p className="text-muted-foreground text-lg">
                Please wait while we confirm your payment.
              </p>
            </>
          )}
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-xl border border-border p-6 sm:p-8 mb-8" data-testid="order-details">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b border-border">
            <div>
              <p className="text-sm text-muted-foreground">Order Number</p>
              <p className="text-xl font-bold mono" data-testid="order-number">{order.order_number}</p>
            </div>
            <div className={`status-badge ${order.status}`}>
              {order.status}
            </div>
          </div>

          {/* Order Timeline */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-xs text-muted-foreground">Ordered</span>
            </div>
            <div className="flex-1 h-1 bg-muted mx-2" />
            <div className="flex flex-col items-center gap-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered' ? 'bg-green-100' : 'bg-muted'}`}>
                <Package className={`h-5 w-5 ${order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered' ? 'text-green-600' : 'text-muted-foreground'}`} />
              </div>
              <span className="text-xs text-muted-foreground">Processing</span>
            </div>
            <div className="flex-1 h-1 bg-muted mx-2" />
            <div className="flex flex-col items-center gap-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${order.status === 'shipped' || order.status === 'delivered' ? 'bg-green-100' : 'bg-muted'}`}>
                <Truck className={`h-5 w-5 ${order.status === 'shipped' || order.status === 'delivered' ? 'text-green-600' : 'text-muted-foreground'}`} />
              </div>
              <span className="text-xs text-muted-foreground">Shipped</span>
            </div>
            <div className="flex-1 h-1 bg-muted mx-2" />
            <div className="flex flex-col items-center gap-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${order.status === 'delivered' ? 'bg-green-100' : 'bg-muted'}`}>
                <CheckCircle className={`h-5 w-5 ${order.status === 'delivered' ? 'text-green-600' : 'text-muted-foreground'}`} />
              </div>
              <span className="text-xs text-muted-foreground">Delivered</span>
            </div>
          </div>

          {/* Items */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4">Order Items</h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-4 py-2">
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                    <Package className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Qty: {item.quantity}
                      {item.variant_label && ` • ${item.variant_label}`}
                    </p>
                  </div>
                  <p className="font-medium">{formatPrice(item.item_total)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal (excl. GST)</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipping</span>
              <span>{order.shipping_cost === 0 ? 'Free' : formatPrice(order.shipping_cost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">GST ({Math.round((order.gst_rate ?? 0.15) * 100)}%)</span>
              <span data-testid="order-gst">{formatPrice(order.gst_amount ?? 0)}</span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
              <span>Total (incl. GST)</span>
              <span data-testid="order-total">{formatPrice(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Tax Invoice */}
        {paymentStatus === 'paid' && (
          <div className="bg-white rounded-xl border border-border p-6 sm:p-8 mb-8" data-testid="tax-invoice">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Tax Invoice</h3>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.print()} data-testid="print-invoice-btn">
                Print / Save PDF
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-foreground">{order.business_name || 'VShoppe'}</p>
                <p className="text-muted-foreground whitespace-pre-line">{order.business_address}</p>
                {order.gst_number ? (
                  <p className="text-muted-foreground mt-1">GST No: <span className="font-medium text-foreground">{order.gst_number}</span></p>
                ) : (
                  <p className="text-muted-foreground mt-1">GST No: <span className="italic">to be confirmed</span></p>
                )}
              </div>
              <div className="sm:text-right">
                <p className="text-muted-foreground">Invoice / Order #</p>
                <p className="font-medium mono">{order.order_number}</p>
                <p className="text-muted-foreground mt-2">Date</p>
                <p className="font-medium">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-NZ') : ''}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border flex justify-between text-sm">
              <span className="text-muted-foreground">Total GST ({Math.round((order.gst_rate ?? 0.15) * 100)}%) included in this invoice</span>
              <span className="font-semibold">{formatPrice(order.gst_amount ?? 0)}</span>
            </div>
          </div>
        )}

        {/* Shipping Info */}
        <div className="bg-white rounded-xl border border-border p-6 sm:p-8 mb-8">
          <h3 className="font-semibold mb-4">Shipping Address</h3>
          <div className="text-muted-foreground">
            <p className="font-medium text-foreground">{order.customer_name}</p>
            <p>{order.shipping_address}</p>
            <p>{order.shipping_city}, {order.shipping_postal_code}</p>
            <p>{order.shipping_country}</p>
            <p className="mt-2">{order.customer_phone}</p>
            <p>{order.customer_email}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8" data-testid="continue-shopping-btn">
              Continue Shopping
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="rounded-full px-8">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
